import { HISTORY_ACTION_MODE } from '@candulabs/craft';
import { capitalizeFirst } from 'canoe';
import React, { useMemo } from 'react';

import { ValueGroup } from '@candu-ai/craft/ui';
import { Size as SizeObject, SizeType, SizeValue, useResponsiveSize } from 'craft/components/Size';
import { sizeTypeToString } from 'craft/components/Size/sizeValues';
import { CssSelectorId } from 'craft/types';
import { getDomNodeFromDocument } from 'craft/utils/viewport';

import { SizeStepper } from './SizeStepper';

interface Props {
  nodeIds: string[];
  property: 'min' | 'max' | 'normal';
  supportsPercentageSize?: boolean;
  supportsAutoSize?: boolean;
  cssSelectorId?: CssSelectorId;
  onSetFixedSize?: (key: string) => void;
}

export const ResponsiveSize = ({
  nodeIds,
  property,
  supportsAutoSize,
  supportsPercentageSize,
  cssSelectorId,
  onSetFixedSize,
}: Props) => {
  const { responsiveSize, placeholderSize, setResponsiveSize } = useResponsiveSize(
    nodeIds,
    cssSelectorId,
  );

  const setThrottledSize = useMemo(
    () => (newSize: SizeObject, key: 'width' | 'height') => {
      if (setResponsiveSize) {
        setResponsiveSize(newSize, HISTORY_ACTION_MODE.THROTTLE, 500, property);

        if (
          onSetFixedSize &&
          newSize[key].type === SizeType.Pixel &&
          typeof newSize[key].value !== 'undefined'
        ) {
          onSetFixedSize(key);
        }
      }
    },
    [setResponsiveSize, property],
  );

  if (!responsiveSize) {
    return <></>;
  }

  const size = responsiveSize[property];
  const placeholders = placeholderSize?.[property];

  const onChange = (key: `width` | `height`) => (newSizeValue: SizeValue) => {
    setThrottledSize({ ...size, [key]: { ...size[key], value: newSizeValue } }, key);
  };

  const onChangeType = (key: 'width' | 'height') => (
    newType: SizeType,
    forceValueChange = true,
  ) => {
    if (newType === SizeType.Auto || newType === SizeType.Default) {
      return setThrottledSize({ ...size, [key]: { type: newType } }, key);
    }

    // get how many pixels the element has to set sensible defaults
    if (newType === SizeType.Pixel) {
      // get the first node dom so that we can switch sizes
      const dom = getDomNodeFromDocument(nodeIds[0]);
      const calculatedValue = (key === 'width' ? dom?.offsetWidth : dom?.offsetHeight) || 100;
      return setThrottledSize(
        {
          ...size,
          [key]: {
            type: newType,
            value: forceValueChange ? calculatedValue : size[key].value || 100,
          },
        },
        key,
      );
    }
    // TODO: come up with some calculations of how much % the column is taking in space.
    return setThrottledSize(
      {
        ...size,
        [key]: { type: newType, value: forceValueChange ? 100 : size[key].value || 100 },
      },
      key,
    );
  };

  const namedProperty = property[0].toUpperCase() + property.substring(1);

  return (
    <>
      <ValueGroup>
        {Object.entries(size).map(([key, { type, value }], idx) => {
          const placeholder = placeholders?.[key as 'width' | 'height'];

          return (
            <SizeStepper
              key={key}
              label={
                property === 'normal'
                  ? capitalizeFirst(key)
                  : `${namedProperty}-${capitalizeFirst(key)}`
              }
              type={type}
              valueSuffix={
                !value && placeholder?.value && type !== SizeType.Auto
                  ? sizeTypeToString(placeholder.type)
                  : undefined
              }
              value={value}
              placeholder={type === SizeType.Auto ? 'Auto' : placeholder?.value?.toString()}
              onChange={onChange(key as `width` | `height`)}
              onChangeType={onChangeType(key as `width` | `height`)}
              name={key}
              shouldShowPercentage={supportsPercentageSize}
              shouldShowAuto={supportsAutoSize}
              inGroupProps={{ isFirst: idx === 0, isLast: idx === 1 }}
              property={property}
              onReset={() => onChange(key as `width` | `height`)(undefined)}
            />
          );
        })}
      </ValueGroup>
    </>
  );
};
